"use client";

import { useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CtaButton } from "@/components/optilase/cta-button";
import { sectionSpacing } from "@/components/optilase/section-spacing";
import { TextLink } from "@/components/optilase/text-link";
import { headingOn, onSurface, type Surface } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/lib/use-media-query";
import { headingLines } from "@/lib/heading-lines";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";

export type PriceCard = {
  id: string;
  name: string;
  price?: string;
  unit?: string;
  monthly?: string;
  asterisk?: string;
  description?: string;
  features?: string[];
  rates?: { label: string; value: string }[];
  learnMoreLabel?: string;
  learnMoreHref?: string;
};

export type PriceCardsViewProps = {
  sectionTitle?: string;
  heading?: string;
  body?: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAction?: string | null;
  cards: PriceCard[];
  layout?: "row" | "beside" | "grid";
  defaultOpen?: boolean;
  footnote?: string;
  spacing?: string;
  surface?: Surface | null;
};

const CARD_ORDER = [
  "order-1 md:order-0",
  "order-2 md:order-0",
  "order-3 md:order-0",
  "order-4 md:order-0",
  "order-5 md:order-0",
  "order-6 md:order-0",
  "order-7 md:order-0",
  "order-8 md:order-0",
];

function ToggleGlyph({
  open,
  size = 24,
  className,
}: {
  open: boolean;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={className}
    >
      <path
        d={open ? "M7 7l10 10M17 7L7 17" : "M12 6v12M6 12h12"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlusIcon({
  open,
  closedClass = "bg-teal-02 text-teal-07",
  sizeClass = "size-9",
}: {
  open: boolean;
  closedClass?: string;
  sizeClass?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full transition-colors",
        sizeClass,
        open ? "bg-teal-07 text-white" : closedClass,
      )}
    >
      <ToggleGlyph open={open} size={24} />
    </span>
  );
}

export default function PriceCardsView({
  sectionTitle,
  heading,
  body,
  ctaLabel,
  ctaHref,
  ctaAction,
  cards,
  layout = "row",
  defaultOpen,
  footnote,
  spacing,
  surface,
}: PriceCardsViewProps) {
  const [openId, setOpenId] = useState<string | null | undefined>(undefined);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const touched = openId !== undefined;
  const currentId = touched ? openId : defaultOpen && isDesktop ? (cards[0]?.id ?? null) : null;
  const beside = layout === "beside";
  const grid = layout === "grid";
  const disc = beside || grid;

  const paragraphs = (body ?? "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  const renderCard = (card: PriceCard, extraClass?: string) => {
    const open = currentId === card.id;
    const expandable = Boolean(card.description || card.features?.length || card.rates?.length);
    return (
      <li
        key={card.id}
        className={cn(
          "rounded-2xl",
          grid ? "p-5 lg:p-7" : "p-5 md:p-7",
          disc ? "bg-white" : "bg-teal-01",
          extraClass,
        )}
      >
        <div className={cn("flex gap-6", grid && !open ? "items-center" : "items-start")}>
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <p className="text-h5 text-grey-08 font-semibold tracking-[-0.01em]">{card.name}</p>
            {card.price ? (
              <div className="flex flex-col">
                {/\d/.test(card.price) ? (
                  <span className="text-grey-06 text-sm leading-snug">From </span>
                ) : null}
                <span className="flex items-end gap-2">
                  <span
                    className={cn(
                      "text-[28px] leading-[1.054] font-bold tracking-[0.02em] lg:text-[32px]",
                      disc ? "text-teal-07" : "text-teal-08",
                    )}
                  >
                    {card.price}
                  </span>{" "}
                  {card.unit ? (
                    <span className="text-grey-06 pb-1 text-sm">{card.unit}</span>
                  ) : null}
                </span>
              </div>
            ) : null}{" "}
            {card.monthly ? (
              <p className="text-grey-06 text-base leading-tight font-semibold">{card.monthly}</p>
            ) : null}{" "}
            {card.asterisk ? (
              <p className="text-grey-06 text-sm leading-tight">{card.asterisk}</p>
            ) : null}
          </div>

          {expandable && disc ? (
            <button
              type="button"
              onClick={() => {
                setOpenId(open ? null : card.id);
                if (!open)
                  track(ANALYTICS_EVENTS.PROCEDURE_SELECTED, {
                    treatment: card.name,
                    location: "price-cards",
                  });
              }}
              aria-expanded={open}
              aria-label={open ? `Hide ${card.name} details` : `Show ${card.name} details`}
              className="shrink-0 cursor-pointer"
            >
              <PlusIcon
                open={open}
                closedClass={grid ? "bg-teal-03 text-teal-08" : "bg-teal-02 text-teal-07"}
                sizeClass={grid ? "size-7 lg:size-9" : "size-9"}
              />
            </button>
          ) : null}
        </div>

        {expandable && !disc && !open ? (
          <button
            type="button"
            onClick={() => {
              setOpenId(card.id);
              track(ANALYTICS_EVENTS.PROCEDURE_SELECTED, {
                treatment: card.name,
                location: "price-cards",
              });
            }}
            aria-expanded={false}
            className="text-teal-08 mt-4 flex cursor-pointer items-center gap-2 text-base font-bold"
          >
            What&rsquo;s included
            <ToggleGlyph open={false} size={20} className="translate-y-px" />
          </button>
        ) : null}

        {expandable ? (
          <div
            inert={!open}
            className={cn(
              "grid",
              touched && "transition-[grid-template-rows] duration-300 ease-out",
              open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col gap-6 px-1 pt-6 pb-1">
                {card.description ? (
                  <p className="text-grey-06 text-base leading-relaxed tracking-[-0.01em]">
                    {card.description}
                  </p>
                ) : null}

                {card.features?.length ? (
                  <ul className="gap-x-8 md:columns-2">
                    {card.features.map((f) => (
                      <li key={f} className="mb-1 flex break-inside-avoid items-start gap-2.5">
                        <svg
                          width="13"
                          height="10"
                          viewBox="0 0 13 10"
                          fill="none"
                          aria-hidden
                          className="text-teal-07 mt-[7px] shrink-0"
                        >
                          <path
                            d="M1 5.2 4.6 8.8 12 1.2"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span className="text-grey-06 text-base leading-[1.56]">{f}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {card.rates?.length ? (
                  <ul className="flex flex-col">
                    {card.rates.map((rate) => (
                      <li
                        key={rate.label}
                        className="border-grey-01 flex items-start justify-between gap-6 border-b py-3 last:border-0"
                      >
                        <span className="text-grey-07 text-base">{rate.label}</span>
                        <span className="text-grey-08 text-base font-semibold">{rate.value}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {card.monthly ? (
                  <p className="text-grey-06 flex items-center gap-2 text-base">
                    Split your payments with
                    <AssetImage
                      src="/assets/logo/humm.png"
                      alt="humm"
                      width={77}
                      height={16}
                      className="h-4 w-auto"
                    />
                  </p>
                ) : null}

                {grid && card.learnMoreLabel ? (
                  <TextLink href={card.learnMoreHref ?? "#"} className="text-teal-07">
                    {card.learnMoreLabel}
                  </TextLink>
                ) : null}

                {disc ? null : (
                  <button
                    type="button"
                    onClick={() => setOpenId(null)}
                    className="text-teal-08 focus-visible:outline-teal-07 -mx-1 flex w-fit cursor-pointer items-center gap-2 rounded px-1 text-base font-bold focus-visible:outline-2 focus-visible:-outline-offset-2"
                  >
                    Close
                    <ToggleGlyph open size={20} className="translate-y-px" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </li>
    );
  };

  const columns = [cards.filter((_, i) => i % 2 === 0), cards.filter((_, i) => i % 2 === 1)];

  const list = grid ? (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4 lg:gap-6">
      {columns.map((column, i) => (
        <ul key={i} className="contents md:flex md:min-w-0 md:flex-1 md:flex-col md:gap-4 lg:gap-6">
          {column.map((card) => renderCard(card, CARD_ORDER[cards.indexOf(card)]))}
        </ul>
      ))}
    </div>
  ) : (
    <ul
      className={cn(
        "flex flex-col gap-4",
        !beside && "md:grid md:grid-cols-2 md:items-start md:gap-8",
      )}
    >
      {cards.map((card) => renderCard(card))}
    </ul>
  );

  if (grid) {
    return (
      <section
        style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
        className={cn(
          "px-5 md:px-10 lg:px-20",
          sectionSpacing(spacing),
          surface ? "" : "bg-teal-02",
        )}
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-11 md:gap-6 lg:gap-11">
          {sectionTitle ? (
            <h2 className={cn("text-section", headingOn(surface, "text-teal-08"))}>
              {headingLines(sectionTitle)}
            </h2>
          ) : null}
          <div className="flex flex-col gap-5 md:gap-4 lg:gap-7">
            {heading ? (
              <h3
                className={cn(
                  "text-h4",
                  onSurface(surface, "text-white", "text-grey-09", "text-grey-09"),
                )}
              >
                {heading}
              </h3>
            ) : null}
            {list}
            {footnote ? <p className="text-grey-06 text-sm leading-relaxed">{footnote}</p> : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(
        "px-5 md:px-10 lg:px-20",
        sectionSpacing(spacing),
        surface ? "" : beside ? "bg-teal-01" : "bg-white",
      )}
    >
      <div
        className={cn(
          "mx-auto max-w-7xl",
          beside
            ? "grid gap-10 lg:grid-cols-[minmax(0,499px)_minmax(0,640px)] lg:justify-between lg:gap-8"
            : "",
        )}
      >
        {beside ? (
          <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-5">
              {heading ? (
                <h2 className={cn("text-section", headingOn(surface, "text-teal-08"))}>
                  {heading}
                </h2>
              ) : null}
              {paragraphs.map((p) => (
                <p
                  key={p}
                  className={cn(
                    "text-base leading-relaxed tracking-[-0.01em] lg:text-lg",
                    onSurface(surface, "text-white/85", "text-grey-07", "text-grey-07"),
                  )}
                >
                  {p}
                </p>
              ))}
            </div>
            {ctaLabel ? (
              <CtaButton
                href={ctaHref}
                action={ctaAction}
                variant="orange"
                className="w-full md:w-fit"
              >
                {ctaLabel}
              </CtaButton>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-6">
          {list}
          {footnote ? <p className="text-grey-06 text-sm leading-relaxed">{footnote}</p> : null}
        </div>
      </div>
    </section>
  );
}
